/**
 * Shared primitives for the Sources surfaces.
 *
 * The Sources list (`OwnedSourcesManager`) and the per-source drill
 * (`SourcePagesPage`) render the same dense single-line table, so the
 * sentiment label, engine-breadth count, host extraction, and sortable
 * header all live here once. Keeping them shared is what makes the drill
 * table visually identical to the list it drills from.
 */

import { consoleRoutes } from '@/lib/routes';

export const ENGINE_ORDER = ['chatgpt', 'claude', 'gemini', 'perplexity'];

export interface SentimentBreakdown {
  positive: number;
  negative: number;
  neutral: number;
}

/** Breadth of citation: how many engines cite this source (a single number). */
export function engineCount(byEngine: Record<string, number>): number {
  return ENGINE_ORDER.filter((e) => (byEngine[e] ?? 0) > 0).length;
}

// `enginePctLabel` and `sentimentLabel` were deleted 2026-07-25 — both had zero
// call sites. They are leftovers from the removed sentiment surface: the
// Sources lists now explicitly omit `sentiment_breakdown`; only detail-facing
// endpoints retain it. `engineCount` above is NOT dead — 7 live call sites.

// `SortHeader` + its `SortDir` type were deleted 2026-07-26 — zero importers.
// It was the hand-rolled sortable column header that `DataTable`'s `HeaderCell`
// replaced (rules 26-27, 28: `[sort] Name [filter]` is normalized and automatic).
// Sort STATE is `useTableSort`/`applySort` from
// `@knitknot/shared/components/ui/table-state`; column sort direction is
// `SortDirection` from `…/data-table`. Never hand-roll either again.

const SOURCE_HASH_RE = /^[a-f0-9]{64}$/i;

function safeDecodePath(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

export function isSourceHashSegment(value: string | null | undefined): boolean {
  return SOURCE_HASH_RE.test(value ?? '');
}

export function parseSourceByDomainPath(pathname: string): { bucket: string; urlHash: string | null } {
  const marker = `${consoleRoutes.data.sources}/domains/`;
  const markerIndex = pathname.indexOf(marker);
  if (markerIndex === -1) return { bucket: '', urlHash: null };

  const rest = pathname.slice(markerIndex + marker.length);
  const segments = rest.split('/').filter(Boolean);
  const lastSegment = segments[segments.length - 1] ?? '';
  if (isSourceHashSegment(lastSegment)) {
    return {
      bucket: safeDecodePath(segments.slice(0, -1).join('/')),
      urlHash: lastSegment,
    };
  }

  return {
    bucket: safeDecodePath(rest.replace(/\/$/, '')),
    urlHash: null,
  };
}

// `HeaderHint` was DELETED 2026-07-26 — a byte-identical fork of the canonical
// `TooltipHeader` that had ended up with MORE call sites (30) than the canonical it
// duplicated, in an app directory rule 51 bans outright. All 30 migrated; the ban is
// unconditional in `check-canonical` CHECK 2m. Use
// `@knitknot/shared/components/ui/tooltip-header` (prop is `content`, not `hint`).
